/**
 * Centralized Realtime Query Invalidation Strategies
 * 
 * Eliminates duplicate invalidation logic across realtime hooks.
 * Each strategy encapsulates the exact queries that need to be invalidated
 * for a specific type of realtime event.
 * 
 * Usage:
 * ```ts
 * const invalidate = useRealtimeInvalidations();
 * invalidate.postEngagement(postId);
 * ```
 */

import { useQueryClient, QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/utils/queryKeys';
import { useCallback } from 'react';

/**
 * Invalidate all post engagement queries (likes, comments)
 */
export function invalidatePostEngagement(
  queryClient: QueryClient,
  postId: string
) {
  // Invalidate post data queries with predicate matching
  queryClient.invalidateQueries({ 
    predicate: (query) => {
      const key = query.queryKey;
      return key[0] === 'post' && key.includes(postId);
    },
    refetchType: 'active'
  });
  
  // Invalidate post comments — only active views need refresh
  queryClient.invalidateQueries({ 
    queryKey: ['post-comments', postId],
    refetchType: 'active'
  });
  
  // Invalidate single-photo-post cache
  queryClient.invalidateQueries({
    queryKey: ['single-photo-post', postId],
    refetchType: 'active'
  });
  
  // NOTE: square-feed and user-posts are NOT invalidated here.
  // Callers that need list-level invalidation handle it themselves:
  // - useNotificationRealtime → invalidate.userPosts(username) for engagement events
  // - useSquareFeedRealtime → invalidate.squareFeed()
}

/**
 * Invalidate photo engagement queries (likes, comments)
 * @param postId - Optional postId for multi-photo posts to invalidate multi-photo-post query
 */
export function invalidatePhotoEngagement(
  queryClient: QueryClient,
  photoId: string,
  postId?: string
) {
  queryClient.invalidateQueries({ 
    queryKey: ['photo-data', photoId], 
    refetchType: 'active'
  });
  
  queryClient.invalidateQueries({ 
    queryKey: ['photo-likers', photoId], 
    refetchType: 'active'
  });
  
  queryClient.invalidateQueries({ 
    queryKey: ['photo-comments', photoId], 
    refetchType: 'active'
  });
  
  // Invalidate photo likers/commenters infinite lists (for modals)
  queryClient.invalidateQueries({
    queryKey: ['photo-likers-infinite', photoId],
    refetchType: 'active'
  });
  queryClient.invalidateQueries({
    queryKey: ['photo-commenters-infinite', photoId],
    refetchType: 'active'
  });
  
  // Invalidate multi-photo-post query when postId is provided
  if (postId) {
    queryClient.invalidateQueries({ 
      queryKey: ['multi-photo-post', postId],
      refetchType: 'active'
    });
  }
}

/**
 * Invalidate comment-related queries
 */
export function invalidateCommentData(
  queryClient: QueryClient,
  commentId: string,
  photoId?: string,
  postId?: string
) {
  // Invalidate only the affected comment's replies
  queryClient.invalidateQueries({ 
    queryKey: ['comment-replies', commentId],
    refetchType: 'active'
  });
  
  // Invalidate parent photo/post comments if known
  if (photoId) {
    queryClient.invalidateQueries({ 
      queryKey: ['photo-comments', photoId], 
      refetchType: 'active'
    });
    queryClient.invalidateQueries({ 
      queryKey: ['photo-data', photoId], 
      refetchType: 'active'
    });
    
    // Invalidate multi-photo-post for photo context (carousel view sync)
    if (postId) {
      queryClient.invalidateQueries({ 
        queryKey: ['multi-photo-post', postId],
        refetchType: 'active'
      });
    }
  }
  
  if (postId) {
    queryClient.invalidateQueries({ 
      queryKey: ['post-comments', postId], 
      refetchType: 'active'
    });
  }
}

/**
 * Invalidate photo grid caches for STRUCTURAL changes only
 * (new photos, deleted photos, reordered photos)
 * NOT for engagement changes - those are handled by post/photo queries
 */
export function invalidatePhotoGridStructure(
  queryClient: QueryClient,
  userId: string
) {
  queryClient.invalidateQueries({
    queryKey: ['photos', userId],
    exact: true,
    refetchType: 'active'
  });
  queryClient.invalidateQueries({
    queryKey: ['posted-photos', userId],
    exact: true,
    refetchType: 'active'
  });
  queryClient.invalidateQueries({
    queryKey: ['avatar-photos', userId],
    exact: true,
    refetchType: 'active'
  });
}

/**
 * Invalidate profile-related queries only
 * OPTIMIZED: No longer invalidates photo grids for engagement changes
 * Photo grids use live queries now (useSinglePhotoPostQuery, useMultiPhotoPostData)
 */
export function invalidateProfileData(
  queryClient: QueryClient,
  userId?: string,
  username?: string
) {
  // Invalidate profile queries (scoped if userId provided)
  if (userId) {
    queryClient.invalidateQueries({
      queryKey: ['profile', userId],
      exact: true,
      refetchType: 'active'
    });
  } else {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'profile';
      },
      refetchType: 'active'
    });
  }
  
  // Invalidate user posts (contains denormalized author_avatar_url)
  if (username) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.user.posts(username),
      refetchType: 'active'
    });
  }
}

/**
 * Invalidate connection-related queries
 */
export function invalidateConnectionData(
  queryClient: QueryClient,
  targetUserId?: string
) {
  // Invalidate connected users list (new infinite query key)
  queryClient.invalidateQueries({
    queryKey: ['connections', 'connected'],
  });

  // Invalidate ALL connection request lists (incoming and outgoing)
  queryClient.invalidateQueries({
    queryKey: ['connections', 'requests'],
    exact: false,
  });

  // Force refetch ALL active user-posts infinite queries (direct prefix match)
  // Using refetchQueries instead of predicate-based invalidateQueries because
  // predicate matching fails to trigger refetches for InfiniteQueryObserver instances
  queryClient.refetchQueries({
    queryKey: queryKeys.user.postsPrefix(),
    type: 'active',
  });

  // Invalidate connection status queries
  if (targetUserId) {
    // Specific invalidation when we know the target user
    queryClient.invalidateQueries({
      queryKey: ['connection-status', targetUserId],
    });
  } else {
    // BROAD invalidation for DELETE events where RLS hides sender_id/receiver_id
    // This ensures ALL active connection-status queries are refetched
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'connection-status';
      },
      refetchType: 'active',
    });
  }
}

/**
 * Invalidate notification queries
 */
export function invalidateNotifications(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: ['notifications'],
    exact: false,
    refetchType: 'active',
  });
}

/**
 * Invalidate square feed queries
 */
export function invalidateSquareFeed(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: queryKeys.square.feed(),
    exact: false,
    refetchType: 'active',
  });
}

/**
 * Invalidate blocked users queries
 */
export function invalidateBlockedUsers(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: ['connections', 'blocked'],
    exact: true,
  });
}

/**
 * Invalidate user posts queries (profile-scoped)
 */
export function invalidateUserPosts(
  queryClient: QueryClient,
  username: string
) {
  queryClient.invalidateQueries({ 
    queryKey: queryKeys.user.posts(username),
    refetchType: 'active'
  });
}

/**
 * Invalidate cross-modal queries (photo modal ↔ post modal sync)
 * OPTIMIZED: No longer invalidates photo grids - they use live queries now
 */
export function invalidateCrossModalSync(
  queryClient: QueryClient,
  postId: string,
  _postOwnerId?: string // Kept for API compat, no longer used for photo grids
) {
  // 1. Single-photo-post query (photo modal)
  queryClient.invalidateQueries({ 
    queryKey: ['single-photo-post', postId],
    exact: true,
    refetchType: 'active'
  });
  
  // 2. Multi-photo-post query
  queryClient.invalidateQueries({ 
    queryKey: ['multi-photo-post', postId],
    exact: true,
    refetchType: 'active'
  });
  
  // 3. Post queries (post modal) - all variations
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return key[0] === 'post' && key.includes(postId);
    },
    refetchType: 'active'
  });
  
  // NOTE: Photo grids no longer invalidated here
  // GridNavigationPhotoModal uses live queries (useSinglePhotoPostQuery, useMultiPhotoPostData)
}

/**
 * Hook for realtime invalidation strategies
 * Direct cache invalidation without deduplication
 */
export function useRealtimeInvalidations() {
  const queryClient = useQueryClient();
  
  return {
    postEngagement: useCallback(
      (postId: string) => invalidatePostEngagement(queryClient, postId),
      [queryClient]
    ),
    
    photoEngagement: useCallback(
      (photoId: string, postId?: string) => invalidatePhotoEngagement(queryClient, photoId, postId),
      [queryClient]
    ),
    
    commentData: useCallback(
      (commentId: string, photoId?: string, postId?: string) => 
        invalidateCommentData(queryClient, commentId, photoId, postId),
      [queryClient]
    ),
    
    profileData: useCallback(
      (userId?: string, username?: string) => 
        invalidateProfileData(queryClient, userId, username),
      [queryClient]
    ),
    
    photoGridStructure: useCallback(
      (userId: string) => invalidatePhotoGridStructure(queryClient, userId),
      [queryClient]
    ),
    
    connectionData: useCallback(
      (targetUserId?: string) => invalidateConnectionData(queryClient, targetUserId),
      [queryClient]
    ),
    
    notifications: useCallback(
      () => invalidateNotifications(queryClient),
      [queryClient]
    ),
    
    squareFeed: useCallback(
      () => invalidateSquareFeed(queryClient),
      [queryClient]
    ),
    
    blockedUsers: useCallback(
      () => invalidateBlockedUsers(queryClient),
      [queryClient]
    ),
    
    userPosts: useCallback(
      (username: string) => invalidateUserPosts(queryClient, username),
      [queryClient]
    ),
    
    crossModalSync: useCallback(
      (postId: string, postOwnerId?: string) => 
        invalidateCrossModalSync(queryClient, postId, postOwnerId),
      [queryClient]
    ),
  };
}
